import { useEffect, useState, useMemo } from "react";
import { PropagateLoader } from "react-spinners";
import Stats from "../organisms/estadisticas/Stats";
import { GET_GASTOS } from "../../api/apiGastos";
import { GET_INMUEBLES } from "../../api/apiInmuebles";
import { GET_EDO_CTA_ALL } from "../../api/apiEdoCuenta";
import { GET_MANTENIMIENTO } from "../../api/apiMantenimiento";
import FilterStats from "../organisms/estadisticas/FilterStats";
import Card from "../atoms/Card";

import moment from "moment";
import { formatCurrency } from "../../utils/formatCurrency";
import DownloadXlsxButton from "../atoms/DownloadXLSXButton";
import { exportEstadisticasToXLSX } from "../../utils/excel/exportEstadisticasToXLSX";
import CardHeader from "../atoms/CardHeader";
import Breadcrumb from "../molecules/Breadcrumb";

const EstadisticasTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [edosCta, setEdosCta] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [mantenimiento, setMantenimiento] = useState([]);
  const [filters, setFilters] = useState({
    inmobiliaria: null,
    dateRange: { start: null, end: null },
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [inmueblesRes, gastosRes, edosCtaRes, mantenimientoRes] =
          await Promise.allSettled([
            GET_INMUEBLES(),
            GET_GASTOS(),
            GET_EDO_CTA_ALL(),
            GET_MANTENIMIENTO(),
          ]);

        if (inmueblesRes.status === "fulfilled") setInmuebles(inmueblesRes.value);
        if (gastosRes.status === "fulfilled") setGastos(gastosRes.value);
        if (edosCtaRes.status === "fulfilled") setEdosCta(edosCtaRes.value);
        if (mantenimientoRes.status === "fulfilled") setMantenimiento(mantenimientoRes.value);

        const errors = [inmueblesRes, gastosRes, edosCtaRes, mantenimientoRes].filter(
          (r) => r.status === "rejected"
        );

        if (errors.length > 0) {
          setError("Ocurrieron errores al cargar algunos datos.");
          console.error(errors);
        }
      } catch (err) {
        setError("Error inesperado al obtener datos.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    let inmueblesFiltered = [...inmuebles];
    let gastosFiltered = [...gastos];
    let edosCtaFiltered = [...edosCta];
    let mantenimientoFiltered = [...mantenimiento];

    if (filters.inmobiliaria) {
      const selected = filters.inmobiliaria.value;
      inmueblesFiltered = inmuebles.filter(
        (i) => String(i.real_estate) === selected
      );

      const ids = inmueblesFiltered.map((i) => i.id);

      gastosFiltered = gastosFiltered.filter((g) => ids.includes(g.propertieId));
      edosCtaFiltered = edosCtaFiltered.filter((e) => ids.includes(e.propertieId));
      mantenimientoFiltered = mantenimientoFiltered.filter((m) => ids.includes(m.propertieId));
    }

    if (filters.dateRange.start && filters.dateRange.end) {
      const start = new Date(filters.dateRange.start);
      const end = new Date(filters.dateRange.end);

      gastosFiltered = gastosFiltered.filter((g) => {
          const d = new Date(g.date);
          return d >= start && d <= end;
      });

      mantenimientoFiltered = mantenimientoFiltered.filter((m) => {
        const d = new Date(m.date_service);
        return d >= start && d <= end;
      });

      edosCtaFiltered = edosCtaFiltered.filter((e) => {
        const [startStr, endStr] = e.month.split(" - ");
        const dStart = new Date(startStr);
        const dEnd = new Date(endStr);
        return dStart <= end && dEnd >= start;
      });
    }

    return {
      inmuebles: inmueblesFiltered,
      gastos: gastosFiltered,
      edosCta: edosCtaFiltered,
      mantenimiento: mantenimientoFiltered,
    };
  }, [filters, inmuebles, gastos, edosCta, mantenimiento]);

  const resumen = useMemo(() => {
    const totalInmuebles = filteredData.inmuebles.length;
    const totalGastos = filteredData.gastos.reduce((acc, g) => acc + (Number(g.amount) || 0), 0);

    const rentedCount = filteredData.inmuebles.filter((inm) => inm.isRented).length;
    const notRentedCount = totalInmuebles - rentedCount;

    const mortgagedCount = filteredData.inmuebles.filter((inm) => inm.isMortgaged).length;
    const notMortgagedCount = totalInmuebles - mortgagedCount;

    const totalRentas = filteredData.edosCta.reduce((acc, r) => acc + (Number(r.total) || 0), 0);
    const totalMantenimiento = filteredData.mantenimiento.reduce((acc, m) => acc + (Number(m.cost) || 0), 0);

    const hoy = moment().startOf("day");
    const getStartDate = (rent) => {
      if (!rent?.month) return null;
      const first = String(rent.month).split(" - ")[0]?.trim();
      return first ? moment(first, "YYYY-MM-DD", true) : null;
    };
    const rentasCobradas = filteredData.edosCta.filter((r) => r.status === true);
    const rentasAtrasadas = filteredData.edosCta.filter((r) => {
      if (r.status) return false;
      const start = getStartDate(r);
      return start?.isValid() && start.isSameOrBefore(hoy, "day");
    });
    const rentasProgramadas = filteredData.edosCta.filter((r) => {
      if (r.status) return false;
      const start = getStartDate(r);
      return start?.isValid() && start.isAfter(hoy, "day");
    });
    const sumTotal = (arr) => arr.reduce((acc, r) => acc + (Number(r.total) || 0), 0);

    return {
      totalInmuebles,
      totalGastos: formatCurrency(totalGastos),
      rentedCount,
      notRentedCount,
      mortgagedCount,
      notMortgagedCount,
      totalMantenimiento: formatCurrency(totalMantenimiento),
      totalRentas: formatCurrency(totalRentas),
      rentasCobradas: { count: rentasCobradas.length, total: formatCurrency(sumTotal(rentasCobradas)) },
      rentasAtrasadas: { count: rentasAtrasadas.length, total: formatCurrency(sumTotal(rentasAtrasadas)) },
      rentasProgramadas: { count: rentasProgramadas.length, total: formatCurrency(sumTotal(rentasProgramadas)) },
    };
  }, [filteredData]);

  const exportFileName = useMemo(() => {
    const parts = ["estadisticas"];
    if (filters.inmobiliaria?.label) parts.push(filters.inmobiliaria.label);
    if (filters.dateRange.start && filters.dateRange.end) {
      parts.push(
        `${moment(filters.dateRange.start).format("YYYYMMDD")}-${moment(filters.dateRange.end).format("YYYYMMDD")}`
      );
    }
    return `${parts.join("_")}.xlsx`;
  }, [filters]);

  const handleExport = async () => {
    const meta = {
      Inmobiliaria: filters.inmobiliaria?.label || "-",
      Rango: (filters.dateRange.start && filters.dateRange.end)
        ? `${moment(filters.dateRange.start).format("YYYY-MM-DD")} a ${moment(filters.dateRange.end).format("YYYY-MM-DD")}`
        : "-",
      Generado: moment().format("YYYY-MM-DD HH:mm"),
    };

    return exportEstadisticasToXLSX({
      filteredData,
      resumen,
      fileName: exportFileName,
      meta,
    });
  };

  if (loading) return ( <div className="w-full h-full flex justify-center items-center"> <PropagateLoader color="red" /> </div> );

  if (error) return ( <div className="w-full h-full flex justify-center items-center text-red-500"> {error} </div> );

  return (
    <div className="flex flex-col gap-4">
      <CardHeader
        left={
          <Breadcrumb
            items={[
              { label: "Inicio", to: "/inicio" },
              { label: "Reportes", to: null },
            ]}
          />
        }
        right={<DownloadXlsxButton onExport={handleExport}/>}
      />
      <Card>
        <FilterStats setFilters={setFilters} />
        <Stats
          dataInmuebles={filteredData.inmuebles}
          dataGastos={filteredData.gastos}
          dataRentas={filteredData.edosCta}
          dataMantenimiento={filteredData.mantenimiento}
          loading={loading}
        />
      </Card>
    </div>
  );
};

export default EstadisticasTemplate;
